/**
 * Organized-tour room cards and "הוספת חדרים" must use the same HR percent
 * as the deal TRACE («אחרי עמלת HR»). The room matrix often prices the LDS
 * net only; the order summary was the only place that kept the deal HR.
 */

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

export function readOrganizedTourDealOurTotalFare(selectedResult) {
  const candidates = [
    selectedResult?.ourPrice?.ourTotalFare,
    selectedResult?.fullDeal?.ourPrice?.ourTotalFare,
    selectedResult?.fareDetails?.ourTotalFare,
    selectedResult?.fullDeal?.fareDetails?.ourTotalFare,
  ];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object") return candidate;
  }
  return null;
}

export function readOrganizedTourDealHrPercent(dealOtf) {
  const pct = num(dealOtf?.hrCommissionPercent ?? dealOtf?.unifiedHrMarginPercent);
  if (pct != null && pct > 0 && pct < 100) return pct;
  const initial = num(dealOtf?.initialPrice);
  const hr = num(dealOtf?.hrCommission ?? dealOtf?.unifiedHrMargin);
  if (initial != null && initial > 0 && hr != null && hr > 0) {
    const derived = (hr / initial) * 100;
    if (derived > 0 && derived < 100) return round2(derived);
  }
  return null;
}

/**
 * After-HR package ILS for one room (no payment credit-card fee).
 * When the room net is the deal net, the deal's rounded after-HR amount is kept.
 */
export function organizedTourRoomPriceWithDealHr(roomOtf, dealOtf) {
  const pct = readOrganizedTourDealHrPercent(dealOtf);
  const initial = num(roomOtf?.initialPrice);
  const pwc = num(roomOtf?.priceWithCommission);
  const handle = Math.max(0, num(roomOtf?.handleFee) ?? 0);
  const base =
    initial != null && initial > 0
      ? initial
      : pwc != null && pwc > 0
        ? round2(pwc - handle)
        : null;
  if (base == null || base <= 0) return null;
  if (!(pct > 0)) return pwc != null && pwc > 0 ? pwc : base;

  const dealInitial = num(dealOtf?.initialPrice);
  const dealAfter = num(dealOtf?.priceWithCommission);
  let expected = round2(base * (1 + pct / 100));
  if (
    dealInitial != null &&
    dealInitial > 0 &&
    Math.abs(base - dealInitial) < 1 &&
    dealAfter != null &&
    dealAfter > 0
  ) {
    expected = dealAfter;
  }

  const packagePart = pwc != null && pwc > 0 ? round2(pwc - handle) : 0;
  if (packagePart + 0.51 >= expected) return pwc;
  return round2(expected + handle);
}

/**
 * Writes the deal HR onto a room fare so room cards, add-room cards, and the
 * room-sum total share one after-HR package price.
 */
/**
 * Room-matrix ILS → the same after-HR package the order summary shows.
 * The selected package room (anchor) becomes `packageIls` exactly.
 * Other occupancies keep the same ratio (single / triple / upgrades).
 * If `rawIls` is already `packageIls`, it stays — safe to run twice on the anchor.
 */
export function scaleOrganizedTourRoomDisplayIls(rawIls, packageIls, anchorRawIls) {
  const raw = num(rawIls);
  const pkg = num(packageIls);
  const anchor = num(anchorRawIls);
  if (raw == null || raw <= 0) return null;
  if (pkg == null || pkg <= 0 || anchor == null || anchor <= 0) return Math.round(raw);
  if (Math.abs(pkg - anchor) <= 0.51) return Math.round(raw);
  if (Math.abs(raw - pkg) <= 0.51) return Math.round(pkg);
  if (Math.abs(raw - anchor) <= 0.51) return Math.round(pkg);
  return Math.round((raw * pkg) / anchor);
}

/** Agent "+" / "%" handling fee shown on each organized-tour room price. */
export function addOrganizedTourAgentFeeToDisplayedIls(roomIls, handleFeeData) {
  const base = num(roomIls);
  if (base == null || base <= 0) return roomIls;
  const feeVal = num(handleFeeData?.value);
  if (feeVal == null || feeVal <= 0) return Math.round(base);
  if (handleFeeData?.type === "%") {
    return Math.round(base * (1 + feeVal / 100));
  }
  if (handleFeeData?.type === "+") {
    return Math.round(base + feeVal);
  }
  return Math.round(base);
}

export function stampOrganizedTourDealHrOnFareDetails(ourFareDetails, dealOtf) {
  const tf = ourFareDetails?.ourTotalFare;
  if (!tf || typeof tf !== "object") return ourFareDetails;
  const afterHr = organizedTourRoomPriceWithDealHr(tf, dealOtf);
  if (!(afterHr > 0)) return ourFareDetails;

  const initial = num(tf.initialPrice);
  const handle = Math.max(0, num(tf.handleFee) ?? 0);
  const packageIls = round2(afterHr - handle);
  tf.priceWithCommission = afterHr;
  tf.priceWithCommissionAndCreditCardFee = afterHr;
  if (initial != null && initial > 0) {
    const hrAmount = round2(packageIls - initial);
    if (hrAmount >= 0) tf.hrCommission = hrAmount;
  }
  const pct = readOrganizedTourDealHrPercent(dealOtf);
  if (pct != null) tf.hrCommissionPercent = pct;
  return ourFareDetails;
}
